// Formatos PERSONALIZADOS de generación — ADR-2026-10-20-formatos-personalizados.
//
// Un formato personalizado es un formato de descarga de la marca (`PerfilDeDescarga`:
// ancho, alto, tipo de archivo y peso máximo, `brand_profiles.descarga_config`) que se
// elige en el selector de formato al generar. Los modelos sólo generan en las siete
// proporciones del catálogo, así que:
//
//   1. se genera en la proporción ESTÁNDAR más parecida (`slugEstandarMasCercano`);
//   2. el pedido lleva el formato (`formato_personalizado`), que el backend guarda en
//      `datos_generacion` con el resto del body;
//   3. al descargar esa imagen se recorta al centro, se redimensiona y se convierte a lo
//      guardado (`formatoDeSalida.convertirImagen`), sin preguntar.
//
// El slug que viaja por los selectores es `perfil:<id>:<estándar>`. El estándar va ADENTRO
// a propósito: quien sólo necesita la proporción (las funciones que arman el pedido, que
// corren sin React y a veces antes de que se carguen los formatos de la marca) la saca del
// slug sin depender de nada. Puro, salvo el registro en memoria de abajo.

import type { FormatoDeSalida } from './formatoDeSalida';

/** Lo mínimo de un formato de descarga que hace falta acá (calza con `PerfilDeDescarga`). */
export interface FormatoPersonalizado {
  id: string;
  nombre: string;
  ancho: number;
  alto: number;
  formato: FormatoDeSalida;
  pesoMaxKB: number | null;
}

/** Lo que viaja en el pedido y queda en `datos_generacion.formato_personalizado`: una
 *  COPIA, para que la imagen se descargue igual aunque después borren el formato. */
export type FormatoPersonalizadoGuardado = FormatoPersonalizado;

/** Las siete proporciones que generan los modelos, con el slug del catálogo. */
export const PROPORCIONES_ESTANDAR: ReadonlyArray<{ slug: string; ancho: number; alto: number }> = [
  { slug: 'post', ancho: 4, alto: 5 },
  { slug: 'story', ancho: 9, alto: 16 },
  { slug: 'square', ancho: 1, alto: 1 },
  { slug: 'portrait', ancho: 2, alto: 3 },
  { slug: 'banner', ancho: 16, alto: 9 },
  { slug: 'editorial', ancho: 3, alto: 2 },
  { slug: 'billboard', ancho: 21, alto: 9 },
];

const PREFIJO = 'perfil:';

/**
 * La proporción estándar que menos imagen pierde al recortarla a `ancho × alto`. Se mide
 * en escala logarítmica: 1:2 y 2:1 están igual de lejos de 1:1, que es lo que pasa al
 * recortar (perder la mitad de los costados o la mitad de arriba cuesta lo mismo).
 */
export function slugEstandarMasCercano(ancho: number, alto: number): string {
  if (!(ancho > 0 && alto > 0)) return 'post';
  const objetivo = Math.log(ancho / alto);
  let mejor = PROPORCIONES_ESTANDAR[0];
  for (const p of PROPORCIONES_ESTANDAR) {
    if (Math.abs(Math.log(p.ancho / p.alto) - objetivo) < Math.abs(Math.log(mejor.ancho / mejor.alto) - objetivo)) {
      mejor = p;
    }
  }
  return mejor.slug;
}

export function esFormatoPersonalizado(slug: string | null | undefined): slug is string {
  return typeof slug === 'string' && slug.startsWith(PREFIJO);
}

export function slugDeFormatoPersonalizado(f: Pick<FormatoPersonalizado, 'id' | 'ancho' | 'alto'>): string {
  return `${PREFIJO}${f.id}:${slugEstandarMasCercano(f.ancho, f.alto)}`;
}

/** El id del formato de un slug personalizado, o `null` si no lo es. */
export function idDeFormatoPersonalizado(slug: string | null | undefined): string | null {
  if (!esFormatoPersonalizado(slug)) return null;
  const id = slug.slice(PREFIJO.length).split(':')[0];
  return id || null;
}

/**
 * El slug ESTÁNDAR con el que se genera: el mismo si ya es uno del catálogo, la proporción
 * guardada en el slug si es personalizado. Es lo que tienen que usar todas las tablas de
 * slug → proporción.
 */
export function formatoEstandarDe(slug: string | null | undefined): string | null {
  if (!esFormatoPersonalizado(slug)) return slug ?? null;
  const estandar = slug.slice(PREFIJO.length).split(':')[1];
  return PROPORCIONES_ESTANDAR.some((p) => p.slug === estandar) ? estandar : 'post';
}

/** "Tiendanube (1000×1500)"; sin nombre, "Personalizado (1000×1500)". */
export function etiquetaDeFormatoPersonalizado(f: Pick<FormatoPersonalizado, 'nombre' | 'ancho' | 'alto'>): string {
  return `${f.nombre.trim() || 'Personalizado'} (${f.ancho}×${f.alto})`;
}

/** Lee `datos_generacion.formato_personalizado` de una fila, o `null` si no hay uno usable. */
export function formatoPersonalizadoDeDatos(datos: unknown): FormatoPersonalizadoGuardado | null {
  if (!datos || typeof datos !== 'object') return null;
  const f = (datos as Record<string, unknown>).formato_personalizado;
  if (!f || typeof f !== 'object') return null;
  const r = f as Record<string, unknown>;
  const ancho = Number(r.ancho);
  const alto = Number(r.alto);
  if (!(ancho > 0 && alto > 0)) return null;
  const formato: FormatoDeSalida = r.formato === 'jpg' || r.formato === 'png' || r.formato === 'webp' ? r.formato : 'jpg';
  const peso = Number(r.pesoMaxKB);
  return {
    id: typeof r.id === 'string' ? r.id : '',
    nombre: typeof r.nombre === 'string' ? r.nombre : '',
    ancho: Math.round(ancho),
    alto: Math.round(alto),
    formato,
    pesoMaxKB: Number.isFinite(peso) && peso > 0 ? peso : null,
  };
}

// ── Registro en memoria ─────────────────────────────────────────────────────
// Los formatos de la marca activa, para lo que necesita más que la proporción (la
// etiqueta, y la copia que viaja en el pedido) y corre sin React. Lo llena
// `formatosPersonalizadosStore`.

const registro = new Map<string, FormatoPersonalizado>();

export function registrarFormatosPersonalizados(formatos: readonly FormatoPersonalizado[]): void {
  registro.clear();
  for (const f of formatos) registro.set(f.id, f);
}

export function formatoPersonalizadoDeSlug(slug: string | null | undefined): FormatoPersonalizado | null {
  const id = idDeFormatoPersonalizado(slug);
  return id ? (registro.get(id) ?? null) : null;
}

/**
 * Lo que se suma al pedido de una generación con un formato personalizado: la copia del
 * formato, que el backend guarda en `datos_generacion`. Vacío con un slug estándar, o si
 * el formato no está en el registro (no se inventa: esa imagen baja como cualquier otra).
 */
export function camposDeFormatoPersonalizado(
  slug: string | null | undefined,
): { formato_personalizado: FormatoPersonalizadoGuardado } | Record<string, never> {
  const f = formatoPersonalizadoDeSlug(slug);
  if (!f) return {};
  return {
    formato_personalizado: {
      id: f.id,
      nombre: f.nombre,
      ancho: f.ancho,
      alto: f.alto,
      formato: f.formato,
      pesoMaxKB: f.pesoMaxKB,
    },
  };
}
